/**
 * Make `alert` / `confirm` / `prompt` behave the SAME everywhere a plugin runs.
 *
 * The production plugin iframe is sandboxed without `allow-same-origin`
 * (`PLUGIN_IFRAME_SANDBOX`), which gives it an opaque origin — so it is
 * cross-origin to the host, and browsers refuse to show its JS dialogs. The
 * call does not throw: `confirm()` returns `false`, `prompt()` returns `null`,
 * `alert()` does nothing. A guard written the obvious way therefore turns into
 * a button that silently does nothing:
 *
 *     if (!window.confirm('Delete this?')) return;   // never proceeds
 *
 * Adding `allow-modals` would not help — Chrome blocks dialogs from
 * cross-origin iframes regardless of that token.
 *
 * What made this expensive is that it CANNOT be reproduced locally. The dev
 * harness adds `allow-same-origin` for its tooling, so the identical dialog
 * works there. Works on my machine, dead in production, and no error in either
 * place. Opportunity Graph shipped both of its Delete buttons and an
 * overwrite guard this way (2026-09-06).
 *
 * So the SDK pins the production behaviour in every environment and says why,
 * once per kind of dialog. The harness now fails the same way production does,
 * which is the only place the developer can act on it.
 *
 * Deliberately NOT a polyfill: there is no honest way to return a user's answer
 * synchronously, and quietly substituting "yes" would auto-confirm deletions.
 * A plugin that needs to ask must ask with its own UI.
 */

/** Marker so a second import (or a re-entrant module graph) cannot double-wrap. */
const INSTALLED = '__fiasSandboxDialogsInstalled';

type Dialogs = {
  alert: (message?: unknown) => void;
  confirm: (message?: string) => boolean;
  prompt: (message?: string, _default?: string) => string | null;
};

/**
 * Replace the three dialog functions on `win` with the values a sandboxed,
 * cross-origin frame actually produces, each logging why once.
 *
 * Idempotent, and a no-op outside a browser (SSR, unit tests without a DOM).
 * Never throws: a frozen or otherwise unwritable `window` property must not
 * take the plugin down at import time.
 */
export function installSandboxDialogShims(
  win: Window | undefined = typeof window === 'undefined' ? undefined : window,
): void {
  if (!win) return;
  // The install marker is not part of the Window type; keep the cast to this
  // one narrow place rather than widening the parameter, which `window` itself
  // does not satisfy.
  const marker = win as unknown as Record<string, unknown>;
  if (marker[INSTALLED]) return;

  const warned = new Set<string>();
  const explain = (name: string): void => {
    if (warned.has(name)) return;
    warned.add(name);
    console.error(
      `[fias-sdk] window.${name}() does nothing in a Fias plugin. The plugin frame is ` +
        `sandboxed with an opaque origin, so the browser refuses its dialogs — ` +
        `${name}() returns ${name === 'confirm' ? 'false' : name === 'prompt' ? 'null' : 'undefined'} ` +
        `without showing anything. Any guard written as ` +
        `\`if (!confirm(...)) return;\` will never proceed in production. Ask with your own UI ` +
        `instead. (The dev harness would otherwise show the dialog and hide this from you.)`,
    );
  };

  const shims: Dialogs = {
    alert: () => {
      explain('alert');
    },
    confirm: () => {
      explain('confirm');
      return false;
    },
    prompt: () => {
      explain('prompt');
      return null;
    },
  };

  try {
    win.alert = shims.alert;
    win.confirm = shims.confirm;
    win.prompt = shims.prompt;
    marker[INSTALLED] = true;
  } catch {
    // A host that froze these is already enforcing the same outcome; nothing
    // to do, and an exception here would break every plugin at import time.
  }
}
